import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Autocomplete, useDebouncedValue } from "@msys/ui";
import { identity } from "lodash-es";
import React from "react";
import {
  OrganisationMembershipsFilters,
  OrganisationMembershipsSorting,
  OrganisationMembershipStatus,
} from "../../../clients/graphqlTypes.js";
import {
  alphabeticSortByLabel,
  memberToMemberOption,
} from "../users/helpers.js";
import { UserAvatarFragment } from "../users/UserAvatar.generated.js";
import { UserAvatar } from "../users/UserAvatar.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
} from "./OrganisationMemberSelect.generated.js";

export const EMPTY_ARRAY: string[] = [];
export const DEFAULT_SORTING = [
  { column: "firstname" as const, direction: "asc" as const },
];
export interface OrganisationMemberSelectProps extends Omit<
  React.ComponentProps<
    typeof Autocomplete<
      {
        label: string;
        value: string;
        membershipStatus: OrganisationMembershipStatus;
        userAvatar: UserAvatarFragment;
      },
      false
    >
  >,
  | "key"
  | "onChange"
  | "value"
  | "renderInput"
  | "options"
  | "renderOption"
  | "getOptionLabel"
> {
  value: string | null;
  onChange: (value: string | null) => void;
  sorting?: Array<OrganisationMembershipsSorting>;
  filters?: OrganisationMembershipsFilters;
}

export const OrganisationMemberSelect = ({
  value,
  onChange,
  inputLabel,
  placeholder,
  required,
  disabled,
  readOnly,
  error,
  sorting = DEFAULT_SORTING,
  filters,
  ...props
}: OrganisationMemberSelectProps) => {
  const [inputValue, setInputValue] = React.useState("");
  const inputValueDebounced = useDebouncedValue(inputValue, 300);

  const client = useApolloClient();
  const memberOptionsQuery = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting,
      filters,
      search: inputValueDebounced,
    },
  });
  const memberOptions = React.useMemo(
    () =>
      (
        getDataOrNull(
          (memberOptionsQuery?.data ?? memberOptionsQuery?.previousData)
            ?.organisationMemberships
        )?.edges.map(edge => edge.node) ?? []
      )
        .map(memberToMemberOption)
        .sort(alphabeticSortByLabel),
    [memberOptionsQuery?.data, memberOptionsQuery?.previousData]
  );

  const selectedMembersQuery = useOrganisationMemberSelect_SelectedMembersQuery(
    {
      client,
      variables: {
        sorting: DEFAULT_SORTING,
        selectedMembers: value ? [value] : EMPTY_ARRAY,
        limit: 1,
      },
      skip: !value,
    }
  );
  const selectedMember = React.useMemo(
    () =>
      getDataOrNull(
        (selectedMembersQuery.data ?? selectedMembersQuery.previousData)
          ?.selectedMembers
      )
        ?.edges.map(edge => edge.node)
        .find(m => m.id === value),
    [selectedMembersQuery.data, selectedMembersQuery.previousData, value]
  );
  const selectedMemberOption = React.useMemo(() => {
    return selectedMember ? memberToMemberOption(selectedMember) : null;
  }, [selectedMember]);

  return (
    <Autocomplete
      loading={memberOptionsQuery.loading}
      inputLabel={inputLabel}
      placeholder={placeholder}
      options={memberOptions}
      value={selectedMemberOption}
      getOptionLabel={option =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={(
        {
          // @ts-ignore
          key,
          ...props
        },
        option
      ) => {
        return (
          <li key={key} {...props}>
            <UserAvatar
              size="xs"
              userAvatar={option.userAvatar}
              style={{ marginRight: "8px" }}
            />
            {option.label}
          </li>
        );
      }}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "reset") {
          setInputValue("");
          return;
        }
        setInputValue(newInputValue);
      }}
      onChange={value => {
        if (readOnly || disabled) return;
        if (value) {
          onChange(value.value);
        } else {
          onChange(null);
        }
      }}
      error={error}
      filterSelectedOptions
      filterOptions={identity}
      {...props}
    />
  );
};
